// ABOUTME: URL-driven deterministic pennant simulation helpers for dev/load checks
// ABOUTME: Generates weighted fake pennants without touching the backend

const DEFAULT_SWISS_SHARE = 0.6;
const DEFAULT_SEED = 'hopp-sim-v1';
const DEFAULT_MAX_COUNT = 1000;
const DEFAULT_SPREAD_X = 152;
const DEFAULT_SPREAD_Z = 132;
const DEFAULT_CENTER_Z = -60;

// Tier distribution for the simulated field: most pennants are "no minigame
// earned" stubs, then a wedge of Bronze, fewer Silver, sprinkled Gold. Tweaked
// so a 500-pennant simulation lands ~50 gold finials — visually rare without
// being absent.
const TIER_WEIGHTS = [
    { tier: 'none',   weight: 0.35 },
    { tier: 'bronze', weight: 0.30 },
    { tier: 'silver', weight: 0.25 },
    { tier: 'gold',   weight: 0.10 },
];

function pickTier(rng) {
    const r = rng();
    let acc = 0;
    for (const { tier, weight } of TIER_WEIGHTS) {
        acc += weight;
        if (r <= acc) return tier;
    }
    return TIER_WEIGHTS[TIER_WEIGHTS.length - 1].tier;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function firstParam(params, names) {
    for (const name of names) {
        const value = params.get(name);
        if (value !== null) return value;
    }
    return null;
}

export function parsePennantSimulationParams(search = '', { maxCount = DEFAULT_MAX_COUNT } = {}) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const rawCount = firstParam(params, ['simulatePennants', 'simulateFlags', 'flags']);
    if (rawCount === null) return null;

    const parsedCount = Number.parseInt(rawCount, 10);
    if (!Number.isFinite(parsedCount)) return null;

    const rawSwissShare = firstParam(params, ['simulateSwissShare', 'swissShare', 'suiShare']);
    const parsedSwissShare = rawSwissShare === null
        ? DEFAULT_SWISS_SHARE
        : Number.parseFloat(rawSwissShare);

    return {
        count: clamp(parsedCount, 0, maxCount),
        swissShare: Number.isFinite(parsedSwissShare)
            ? clamp(parsedSwissShare, 0, 1)
            : DEFAULT_SWISS_SHARE,
        seed: params.get('simulateSeed') || DEFAULT_SEED,
        homeTeam: params.get('simulateHomeTeam') || 'SUI',
    };
}

function hashSeed(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function makeRng(seed) {
    let state = hashSeed(seed) || 1;
    return () => {
        state = Math.imul(1664525, state) + 1013904223;
        return (state >>> 0) / 4294967296;
    };
}

function shuffle(items, rng) {
    for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
}

function randomBetween(rng, min, max) {
    return min + rng() * (max - min);
}

function simulatedPosition(rng, spreadX, spreadZ) {
    // Uniform scatter across the whole visible field so a large simulation fills
    // the space evenly with plenty of random variation (rather than clustering
    // in a central disc).
    return {
        x: randomBetween(rng, -spreadX, spreadX),
        y: DEFAULT_CENTER_Z + randomBetween(rng, -spreadZ, spreadZ),
    };
}

function buildWeightedTeamCodes(teams, { count, swissShare, homeTeam }, rng) {
    const presentCodes = new Set(teams.map((team) => team.code));
    const homeCode = presentCodes.has(homeTeam) ? homeTeam : teams[0]?.code;
    const others = teams.map((team) => team.code).filter((code) => code !== homeCode);
    const homeCount = homeCode ? Math.round(count * swissShare) : 0;
    const codes = [];

    for (let i = 0; i < Math.min(count, homeCount); i++) {
        codes.push(homeCode);
    }
    while (codes.length < count) {
        codes.push(others[Math.floor(rng() * others.length)] ?? homeCode);
    }

    return shuffle(codes, rng);
}

export function generateSimulatedPennants(teams, {
    count,
    swissShare = DEFAULT_SWISS_SHARE,
    seed = DEFAULT_SEED,
    homeTeam = 'SUI',
    spreadX = DEFAULT_SPREAD_X,
    spreadZ = DEFAULT_SPREAD_Z,
} = {}) {
    if (!Array.isArray(teams) || teams.length === 0) {
        throw new Error('generateSimulatedPennants: teams must be a non-empty array');
    }

    const safeCount = Math.max(0, Math.floor(count ?? 0));
    const rng = makeRng(seed);
    const teamCodes = buildWeightedTeamCodes(teams, {
        count: safeCount,
        swissShare: clamp(swissShare, 0, 1),
        homeTeam,
    }, rng);
    const now = Date.now();

    return teamCodes.map((team, index) => {
        const position = simulatedPosition(rng, spreadX, spreadZ);
        return {
            id: `sim-${seed}-${index}`,
            x: position.x,
            y: position.y,
            surface_y: 0,
            team,
            rotation: (rng() - 0.5) * 0.17,
            tier: pickTier(rng),
            // Spread ages across ~45 seconds so the fresh-pole glow is
            // demonstrable against the dev glow window (~20s): recent poles glow
            // warm and fade out, older ones don't. (Real pennants carry the
            // server's created_at; this only shapes the dev simulation.)
            created_at: new Date(now - Math.round((index / Math.max(1, safeCount - 1)) * 45 * 1000)).toISOString(),
        };
    });
}
